import { useState, useEffect } from 'react';
import backendApi from '../services/backendApi';

export const KNOWN_SITE_NAMES: Record<string, string> = {
  'axon001': 'Devikot Bhakrani',
  'axon002': 'Devikot Rasala',
  'axon003': 'Devikot Sadak',
  'axon004': 'Bhagwa01',
  'axon005': 'Bhagwa02',
  'axon006': 'Khomthal Solar - Roofsur',
  'axon007': 'Pratap Pura',
  'axon008': 'Asadi Solar',
  'axon009': 'Mekuba Solar',
  'axon010': 'Khomthal Solar',
  'axon011': 'Bhikhodai Solar',
  'axon012': 'Dholsar AG',
  'axon013': 'Bandhewa AG Rajmathai',
  'axon014': 'Hariyasar Malinath',
  'axon015': 'Hariyasar Naganrai',
  'axon016': 'TIKTIYA',
  'axon017': 'TIKTIYA',
  'axon018': 'Site 1',
  'axon019': 'Test Site Cascade',
  'axon020': 'Site 2',
  'adani001': 'Adani Khavda Solar 01',
  'apex-solar-01': 'Apex Solar Park - Block A',
  'apex-solar-02': 'Apex Solar Park - Block B',
  'zenith-solar-01': 'Zenith Solar Park - Block 1',
  'zenith-wind-02': 'Zenith Wind Farm - Block 2',
  // Common aliases
  'bhakrani': 'Devikot Bhakrani',
  'rasala': 'Devikot Rasala',
  'sadak': 'Devikot Sadak',
  'bhagwa01': 'Bhagwa01',
  'bhagwa02': 'Bhagwa02',
  'devikot bhakrani': 'Devikot Bhakrani',
  'devikot rasala': 'Devikot Rasala',
  'devikot sadak': 'Devikot Sadak',
};

const dynamicSiteCache = new Map<string, string>();

/**
 * Synchronous lookup for site display name.
 * Checks known fallback dictionary, dynamic cache, and session/local storage.
 */
export const getSiteDisplayName = (siteCode?: string | null, fallbackName?: string | null): string => {
  if (fallbackName && fallbackName.trim() && !fallbackName.toUpperCase().startsWith('AXON')) {
    return fallbackName.trim();
  }

  if (!siteCode) return fallbackName || 'Devikot Bhakrani';

  const clean = siteCode.toLowerCase().trim();

  if (KNOWN_SITE_NAMES[clean]) {
    return KNOWN_SITE_NAMES[clean];
  }

  if (dynamicSiteCache.has(clean)) {
    return dynamicSiteCache.get(clean)!;
  }

  // Check sessionStorage / localStorage for tenant_name if tenant matches
  try {
    const sessionTenant = (sessionStorage.getItem('tenant') || '').toLowerCase().trim();
    const sessionName = sessionStorage.getItem('tenant_name');
    if (sessionTenant === clean && sessionName) {
      dynamicSiteCache.set(clean, sessionName);
      return sessionName;
    }

    const localTenant = (localStorage.getItem('currentSite') || '').toLowerCase().trim();
    const localName = localStorage.getItem('tenant_name');
    if (localTenant === clean && localName) {
      dynamicSiteCache.set(clean, localName);
      return localName;
    }
  } catch {
    // Storage access blocked or unavailable
  }

  return fallbackName || siteCode;
};

/**
 * React hook to get and automatically resolve the site display name.
 * Returns synchronous name immediately (no flicker) and fetches if unknown.
 */
export const useSiteDisplayName = (siteCode?: string | null, initialName?: string | null): string => {
  const [displayName, setDisplayName] = useState<string>(() =>
    getSiteDisplayName(siteCode, initialName)
  );

  useEffect(() => {
    const syncName = getSiteDisplayName(siteCode, initialName);
    setDisplayName(syncName);

    if (!siteCode) return;
    const clean = siteCode.toLowerCase().trim();

    // If already resolved to a known name, no network request needed
    if (KNOWN_SITE_NAMES[clean] || dynamicSiteCache.has(clean)) {
      return;
    }

    let isMounted = true;

    backendApi.getSites(false, siteCode).then((res: any) => {
      if (!isMounted) return;
      if (res && res.success && Array.isArray(res.sites)) {
        const match = res.sites.find(
          (s: any) => (s.site_code || '').toLowerCase().trim() === clean
        );
        if (match?.site_name) {
          dynamicSiteCache.set(clean, match.site_name);
          setDisplayName(match.site_name);
        }
      }
    }).catch(() => {
      // Silently continue with current fallback
    });

    return () => {
      isMounted = false;
    };
  }, [siteCode, initialName]);

  return displayName;
};
